import { useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import { getPlans } from "../../services/Plans";
import StartYourFreeTrial from "../../components/StartYourFreeTrial";
import { Helmet } from "react-helmet-async";
import { checkServerHealth, checkTicketServerHealth, HealthCheck } from "../../services/Health";

const Pricing = () => {
    const navigate = useNavigate();
    const [standardPlans, setStandardPlans] = useState([]);
    const [premiumPlans, setPremiumPlans] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        wakeUpServer();
    }, []);

    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    const wakeUpServer = async () => {
        const isAlive = await checkServerHealth();

        if (isAlive) console.log("Server is alive");
        else console.log("Server unavailable");


        const isTicketAlive = await checkTicketServerHealth();

        if (isTicketAlive) console.log("Ticket Server is alive");
        else console.log("Ticket Server unavailable");
    }

    const waitForServer = async (healthCheck: HealthCheck, serverName: string): Promise<boolean> => {
        const delayTime = 5000;

        while (true) {
            try {
                const isAlive = await healthCheck();

                if (isAlive) {
                    console.log(`${serverName} is alive`);
                    return true;
                }

                console.log(`${serverName} not ready, retrying...`);
            } catch (error) {
                console.log(
                    `${serverName} health check error, retrying...`,
                    error
                );
            }

            await delay(delayTime);
        }
    }

    useEffect(() => {
        const attemptPlans = async () => {
            const ticketServerAlive = await waitForServer(checkTicketServerHealth, "Ticket Server");
            const serverAlive = await waitForServer(checkServerHealth, "Server");

            if (ticketServerAlive && serverAlive) {
                getPlans()
                    .then(data => {
                        const standard = data.filter((plan: any) => plan.planName.includes("Standard"));
                        const premium = data.filter((plan: any) => plan.planName.includes("Premium"));

                        standard.sort((a: any, b: any) => a.billingCycle.localeCompare(b.billingCycle));
                        premium.sort((a: any, b: any) => a.billingCycle.localeCompare(b.billingCycle));

                        setStandardPlans(standard);
                        setPremiumPlans(premium);
                    })
                    .catch(error => console.error('Error fetching plans data:', error))
                    .finally(() => setLoading(false));
            }
        }

        attemptPlans();

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const handleCheckout = (plan: any) => {
        navigate('/checkout', {
            state: {
                planId: plan.id,
                planName: plan.planName,
                price: plan.price,
                billingCycle: plan.billingCycle,
                source: 'pricing'
            }
        });
    };

    const renderPlanDetails = (plan: any) => (
        <div key={plan.id} className="md:w-1/2 lg:w-1/2">
            <p className="font-[cursive] text-xl md:text-2xl text-[#597820]">{plan.description}</p>
            <p className="text-xl md:text-3xl font-bold my-2 text-secondary">
                ${plan.price}/{plan.billingCycle.toLowerCase()}
            </p>
            <button
                className="mb-3 bg-secondary text-white rounded-md py-2 px-4 text-sm md:text-base uppercase hover:bg-blue-900 hover:cursor-pointer"
                onClick={() => handleCheckout(plan)}
            >
                Start Membership
            </button>
        </div>
    );

    const renderPlans = (plans: any) => (
        <div className="md:flex flex-nowrap">
            {plans.map((plan: any) => renderPlanDetails(plan))}
        </div>
    );

    const Loader = () => (
        <div className="flex justify-center items-center">
            <svg
                className="animate-spin h-8 w-8 mr-3 text-secondary"
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
            >
                <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                ></circle>
                <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
            </svg>
            <span className="text-xl font-semibold text-secondary">Loading...</span>
        </div>
    );

    return (
        <>
            <Helmet>
                <title>My Church Directory Pricing | Online Church Directory Software</title>

                <meta
                    name="description"
                    content="Explore powerful church directory features including member management, mobile apps, email and text messaging, printable directories, and secure online access. Manage your church directory with ease."
                />
            </Helmet>

            <section className="bg-slate-50">
                <div className="max-w-7xl mx-auto px-6 py-20 grid lg:grid-cols-2 gap-12 items-center">

                    <div>
                        <span className="inline-flex rounded-full bg-sky-100 text-secondary px-4 py-1 text-sm font-medium">
                            Pricing
                        </span>

                        <h1 className="different_heading mt-5 text-4xl lg:text-5xl font-bold text-slate-900">
                            Simple & Transparent Pricing
                        </h1>

                        <p className="mt-6 text-lg text-slate-600 leading-8">
                            Choose the plan that best fits your church. No hidden fees, flexible
                            billing, and a free 30-day trial to get started.
                        </p>
                    </div>

                    <div>
                        <img
                            src="../images/smallChurch.jpg"
                            alt="Church Pricing"
                            className="rounded-3xl shadow-xl w-full"
                        />
                    </div>

                </div>
            </section>

            <section className="py-20">
                <div className="max-w-7xl mx-auto px-6">

                    <div className="text-center mb-14">
                        <h2 className="different_heading text-3xl font-bold">
                            Choose Your Membership
                        </h2>

                        <p className="mt-3 text-slate-600">
                            Flexible plans designed for churches of every size.
                        </p>
                    </div>

                    <div className="grid lg:grid-cols-2 gap-10">

                        <div className="rounded-3xl border border-slate-200 bg-white shadow-sm p-8">

                            <h3 className="different_heading text-2xl font-bold text-center mb-8">
                                Standard Membership
                            </h3>

                            {loading ? <Loader /> : renderPlans(standardPlans)}

                        </div>

                        <div className="rounded-3xl border border-slate-200 bg-white p-8">

                            <div className="inline-flex rounded-full bg-sky-100 text-secondary px-3 py-1 text-sm font-medium mb-4">
                                Most Popular
                            </div>

                            <h3 className="different_heading text-2xl font-bold text-center mb-8">
                                Premium Membership
                            </h3>

                            {loading ? <Loader /> : renderPlans(premiumPlans)}

                        </div>

                    </div>

                </div>
            </section>

            <section className="bg-slate-50 py-20">
                <div className="max-w-7xl mx-auto px-6">

                    <div className="text-center mb-14">
                        <h2 className="different_heading text-3xl font-bold">
                            Why Churches Choose Us
                        </h2>
                    </div>

                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">

                        <div className="rounded-2xl bg-white border p-6">
                            <h3 className="font-semibold text-lg mb-3">
                                30-Day Free Trial
                            </h3>

                            <p className="text-slate-600">
                                Try every feature free for 30 days before choosing a plan.
                            </p>
                        </div>

                        <div className="rounded-2xl bg-white border p-6">
                            <h3 className="font-semibold text-lg mb-3">
                                No Contracts
                            </h3>

                            <p className="text-slate-600">
                                Cancel, upgrade, or downgrade whenever you need.
                            </p>
                        </div>

                        <div className="rounded-2xl bg-white border p-6">
                            <h3 className="font-semibold text-lg mb-3">
                                Unlimited Members
                            </h3>

                            <p className="text-slate-600">
                                Grow your congregation without worrying about pricing tiers.
                            </p>
                        </div>

                        <div className="rounded-2xl bg-white border p-6">
                            <h3 className="font-semibold text-lg mb-3">
                                Free Member Access
                            </h3>

                            <p className="text-slate-600">
                                Members can access the mobile app and directory at no extra cost.
                            </p>
                        </div>

                        <div className="rounded-2xl bg-white border p-6">
                            <h3 className="font-semibold text-lg mb-3">
                                Affordable Text Messaging
                            </h3>

                            <p className="text-slate-600">
                                Pay only for the messages you send—no monthly messaging fees.
                            </p>
                        </div>

                        <div className="rounded-2xl bg-white border p-6">
                            <h3 className="font-semibold text-lg mb-3">
                                Premium Branding
                            </h3>

                            <p className="text-slate-600">
                                Customize your logo, colors, and branding with Premium.
                            </p>
                        </div>

                    </div>

                </div>
            </section>

            <section className="py-20">
                <div className="max-w-6xl mx-auto px-6 grid lg:grid-cols-2 gap-12 items-center">

                    <div>
                        <img
                            src="../images/billing-timeline.png"
                            alt="Billing Timeline"
                            className="rounded-2xl shadow-lg"
                        />
                    </div>

                    <div>

                        <h2 className="different_heading text-3xl font-bold">
                            Transparent Billing
                        </h2>

                        <p className="mt-6 text-slate-600 leading-8">
                            We believe pricing should be simple. You'll receive reminders before
                            billing, and you can cancel anytime without hidden fees or long-term
                            commitments.
                        </p>

                        <ul className="mt-8 space-y-3 text-slate-700">
                            <li>✓ 30-day free trial</li>
                            <li>✓ Email reminders before billing</li>
                            <li>✓ Monthly & yearly plans</li>
                            <li>✓ Cancel anytime</li>
                        </ul>

                    </div>

                </div>
            </section>
            <StartYourFreeTrial />
        </>
    );
}

export default Pricing;